/**
 * One hook per resource. Screens import hooks, never the client.
 *
 * The invalidation graph lives here, in one place, because the non-obvious edges are
 * exactly the ones a hand-rolled layer forgets:
 *
 *   inviteMember      -> members, invites, subscription   (seatCount moves, so the header moves)
 *   removeMember      -> members, subscription            (frees a seat; the over-limit banner may clear)
 *   updateMemberRole  -> members, me                      (an admin who demoted THEMSELVES loses the nav)
 *   savePreferences   -> me, usage                        (usageMetrics off => the usage cards disappear)
 *   policy write      -> policy, policyAudit
 *   handoff return    -> subscription, entitlements, invoices
 *   checkout success  -> subscription, entitlements, invoices, me
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { usePortalClient } from "../api/PortalClientProvider.js";
import { useSession } from "../auth/SessionProvider.js";
import { qk, CACHE } from "./keys.js";
import {
  fromBilling,
  fromEntitlements,
  type SubscriptionState,
} from "../api/normalise.js";
import * as identity from "../api/generated/identity.generated.js";
import * as billing from "../api/generated/billing.generated.js";
import * as policyApi from "../api/generated/policy.generated.js";
import * as ent from "../api/generated/entitlements.generated.js";
import * as metrics from "../api/generated/metrics.generated.js";
import type { PortalClient } from "../api/client.js";

// ---------------------------------------------------------------- subscription state

/**
 * The shell's status source. Admins read Billing; members read Entitlements. Both are
 * normalised to one internal model, and NOTHING downstream branches on which answered.
 */
export function useSubscriptionState() {
  const client = usePortalClient();
  const { orgId, role } = useSession();
  const isAdmin = role === "admin";

  const admin = useQuery({
    queryKey: qk.subscription(orgId ?? ""),
    enabled: isAdmin && Boolean(orgId),
    ...CACHE.subscription,
    queryFn: async (): Promise<SubscriptionState> =>
      fromBilling(await client.get(billing.getSubscription, "shell.subscription")),
  });

  const member = useQuery({
    queryKey: qk.entitlements(),
    enabled: !isAdmin,
    ...CACHE.entitlements,
    queryFn: async (): Promise<SubscriptionState> =>
      fromEntitlements(await client.get(ent.getMyEntitlements, "shell.subscription")),
  });

  return isAdmin ? admin : member;
}

// ---------------------------------------------------------------- identity

export function useUpdateProfile() {
  const client = usePortalClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) =>
      client.get(identity.updateMe, "account.save", { body: { displayName } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.me() }),
  });
}

export function useMembers() {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: qk.members(orgId ?? ""),
    enabled: Boolean(orgId),
    ...CACHE.members,
    queryFn: () =>
      client.get<identity.ListMembersResponse>(identity.listMembers, "team.members", {
        path: { orgId: orgId! },
      }),
  });
}

export function useInvites(enabled: boolean) {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: qk.invites(orgId ?? ""),
    enabled: enabled && Boolean(orgId),
    ...CACHE.invites,
    queryFn: () =>
      client.get<identity.ListInvitesResponse>(identity.listInvites, "team.members", {
        path: { orgId: orgId! },
      }),
  });
}

export function useInviteMember() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (body: { email: string; role: identity.Role }) =>
      client.get(identity.createInvite, "team.invite", { path: { orgId: orgId! }, body }),
    // The seat bar and the header pill both move when a seat is consumed.
    onSuccess: () => invalidateSeats(qc, orgId!),
  });
}

export function useRevokeInvite() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (inviteId: string) =>
      client.get(identity.revokeInvite, "team.invite", { path: { orgId: orgId!, inviteId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.invites(orgId!) }),
  });
}

export function useUpdateMemberRole() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (v: { userId: string; role: identity.Role }) =>
      client.get(identity.updateMemberRole, "team.role", {
        path: { orgId: orgId!, userId: v.userId },
        body: { role: v.role },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.members(orgId!) });
      // The subtle edge: an admin who demoted THEMSELVES must lose the Billing nav item
      // immediately, not on the next focus.
      void qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useRemoveMember() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (userId: string) =>
      client.get(identity.removeMember, "team.remove", { path: { orgId: orgId!, userId } }),
    onSuccess: () => invalidateSeats(qc, orgId!),
  });
}

// ---------------------------------------------------------------- billing

export function useInvoices(enabled: boolean) {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useInfiniteQuery({
    queryKey: qk.invoices(orgId ?? ""),
    enabled: enabled && Boolean(orgId),
    ...CACHE.invoices,
    initialPageParam: undefined as string | undefined,
    // Append-only: "Load more" adds a page. NEVER refetch page 1 to add page 2 — that is
    // how a paying customer briefly sees an empty invoice table.
    getNextPageParam: (last: billing.ListInvoicesResponse) => (last.hasMore ? last.nextCursor : undefined),
    queryFn: ({ pageParam }) =>
      client.get<billing.ListInvoicesResponse>(billing.listInvoices, "billing.invoices", {
        query: pageParam ? { startingAfter: pageParam } : {},
      }),
  });
}

// ---------------------------------------------------------------- policy

export function usePolicy(enabled = true) {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: qk.policy(orgId ?? ""),
    enabled: enabled && Boolean(orgId),
    ...CACHE.policy,
    queryFn: () =>
      client.get<policyApi.GetOrgPolicyResponse>(policyApi.getOrgPolicy, "policy.read", {
        path: { orgId: orgId! },
      }),
  });
}

export function usePolicyAudit(enabled: boolean) {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: qk.policyAudit(orgId ?? "", null),
    enabled: enabled && Boolean(orgId),
    ...CACHE.policy,
    queryFn: () =>
      client.get<policyApi.GetPolicyAuditResponse>(policyApi.getPolicyAudit, "policy.read", {
        path: { orgId: orgId! },
      }),
  });
}

/**
 * The one write in the portal where optimistic UI is BANNED. Policy fails closed: a 503
 * means the write did not persist, and an optimistic UI would already have claimed it did.
 */
export function useSavePolicy() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (v: { overrides: unknown; version: number }) =>
      client.get<policyApi.GetOrgPolicyResponse>(policyApi.putOrgPolicy, "policy.write", {
        path: { orgId: orgId! },
        body: v.overrides,
        ifMatch: String(v.version),
      }),
    onSuccess: () => invalidatePolicy(qc, orgId!),
  });
}

export function useResetPolicy() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: () =>
      client.get<policyApi.GetOrgPolicyResponse>(policyApi.resetOrgPolicy, "policy.reset", {
        path: { orgId: orgId! },
      }),
    onSuccess: () => invalidatePolicy(qc, orgId!),
  });
}

// ---------------------------------------------------------------- api keys

export function useKeys() {
  const client = usePortalClient();
  const { orgId } = useSession();
  return useQuery({
    queryKey: qk.keys(orgId ?? ""),
    enabled: Boolean(orgId),
    ...CACHE.keys,
    queryFn: () =>
      client.get<ent.ListKeysResponse>(ent.listKeys, "keys.list", { path: { orgId: orgId! } }),
  });
}

export function useCreateKey() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (body: ent.CreateKeyRequest) =>
      client.get<ent.CreatedKey>(ent.createKey, "keys.create", { path: { orgId: orgId! }, body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.keys(orgId!) }),
  });
}

export function useRotateKey() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (v: { keyId: string; body: ent.RotateKeyRequest }) =>
      client.get<ent.CreatedKey>(ent.rotateKey, "keys.rotate", {
        path: { orgId: orgId!, keyId: v.keyId },
        body: v.body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.keys(orgId!) }),
  });
}

export function useRevokeKey() {
  const client = usePortalClient();
  const qc = useQueryClient();
  const { orgId } = useSession();
  return useMutation({
    mutationFn: (keyId: string) =>
      client.get(ent.revokeKey, "keys.revoke", { path: { orgId: orgId!, keyId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.keys(orgId!) }),
  });
}

// ---------------------------------------------------------------- usage

/** The window sent to the server: the current ISO week, Monday to today. */
export function currentWeek(now = new Date()): { from: string; to: string } {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const dayFromMonday = (d.getUTCDay() + 6) % 7;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - dayFromMonday);
  return { from: iso(monday), to: iso(d) };
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Usage.
 *
 * [CORRECTION] docs/USAGE_WIDGETS.md §2 describes the call as taking no parameters. The
 * merged contract's `from` and `to` are BOTH REQUIRED — the "no params" claim in the design
 * is about org/user scoping ("the caller cannot narrow to anyone but themselves"), which is
 * still true. We send the current ISO week and RENDER the boundaries from the response's
 * `range`, never from the browser's clock, so a user in another timezone never sees a week
 * that disagrees with the number.
 *
 * `enabled` carries the consent coupling: when the usage-metrics preference is off the
 * portal does not call the endpoint AT ALL. A consent that still sends the request is
 * theatre.
 */
export function useUsage(opts: { enabled: boolean }) {
  const client = usePortalClient();
  const week = currentWeek();
  return useQuery({
    queryKey: qk.usage(),
    enabled: opts.enabled,
    ...CACHE.usage,
    queryFn: () =>
      client.get<metrics.GetPortalUsageResponse>(metrics.getPortalUsage, "usage", { query: week }),
  });
}

// ---------------------------------------------------------------- invalidation

function invalidateSeats(qc: QueryClient, orgId: string): void {
  void qc.invalidateQueries({ queryKey: qk.members(orgId) });
  void qc.invalidateQueries({ queryKey: qk.invites(orgId) });
  void qc.invalidateQueries({ queryKey: qk.subscription(orgId) });
  void qc.invalidateQueries({ queryKey: qk.entitlements() });
}

function invalidatePolicy(qc: QueryClient, orgId: string): void {
  void qc.invalidateQueries({ queryKey: qk.policy(orgId) });
}

/**
 * Called when focus returns from a Stripe tab, and after a checkout confirms.
 *
 * Under the handoff model every mutating billing control resolves on Stripe's site, so a
 * refetch is the ONLY way the portal learns anything changed. Missing it means the customer
 * returns to a screen still showing their old plan — which reads as the change having failed.
 */
export function invalidateAfterHandoff(qc: QueryClient, orgId: string): void {
  void qc.invalidateQueries({ queryKey: qk.subscription(orgId) });
  void qc.invalidateQueries({ queryKey: qk.entitlements() });
  void qc.invalidateQueries({ queryKey: qk.invoices(orgId) });
}

export function invalidateAfterCheckout(qc: QueryClient, orgId: string): void {
  invalidateAfterHandoff(qc, orgId);
  void qc.invalidateQueries({ queryKey: qk.me() });
}

// ---------------------------------------------------------------- stripe handoffs

/**
 * Two patterns, and the difference is deliberate.
 *
 *   portal-link      -> NEW TAB. A side errand: the customer keeps the portal open, so
 *                       there is no back-button ambiguity and no lost state.
 *   checkout-session -> FULL REDIRECT. Checkout is the primary path with its own back-out;
 *                       a new tab would create two live checkout contexts and an orphaned
 *                       summary tab still saying "Continue to payment".
 *
 * Both URLs are ONE-TIME: created on click, never cached, never rendered into an href at
 * render time, never reused. A portal-link sitting in the DOM is a live billing session
 * waiting to be shared.
 */
export async function openPortalLink(client: PortalClient): Promise<"opened" | "blocked"> {
  const { url } = await client.get<billing.PortalLinkResponse>(
    billing.createPortalLink,
    "billing.portalLink",
  );
  // noopener is required: without it the opened tab can reach back through window.opener.
  const win = window.open(url, "_blank", "noopener");
  return win ? "opened" : "blocked";
}

export async function startCheckout(
  client: PortalClient,
  body: billing.CheckoutSessionRequest,
): Promise<void> {
  const { url } = await client.get<billing.CheckoutSessionResponse>(
    billing.createCheckoutSession,
    "checkout.session",
    { body },
  );
  window.location.assign(url);
}
